import { ReactNode, useContext } from 'react';

import { Theme, WithStyles, createStyles, withStyles } from '@material-ui/core';

import { RiArrowDownSLine } from '@remixicon/react';
import classNames from 'classnames';

import { useAnalytics } from '@backstage/core-plugin-api';
import { Text } from '@backstage/ui';

import { DateUtils } from '@spotify/confidence-core-react';
import { IdentityRefFragment } from '@spotify/confidence-graphql';
import { IdentityAvatar } from '@spotify/plugin-confidence-iam-react';

import { ActivityItemContext } from './ActivityItemContext';

const styles = (theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      alignItems: 'center',
      cursor: 'pointer',
      padding: theme.spacing(2),
      // We do not use "gap" here to space, because we opt for
      // visual alignment instead of logical alignment:
      //  - 2 right margin on the avatar
      //  - 0 margin on the button
    },
    avatar: {
      width: theme.spacing(4),
      height: theme.spacing(4),
      marginRight: theme.spacing(2),
      flexShrink: 0,
    },
    content: {
      flex: 1,
      minWidth: 0,
    },
    icon: {
      marginLeft: theme.spacing(1),
      transition: theme.transitions.create('transform', {
        duration: theme.transitions.duration.shorter,
        easing: theme.transitions.easing.easeOut,
      }),
      '&[aria-expanded="true"]': {
        transform: 'rotate(180deg)',
      },
      flexShrink: 0,
    },
  });

export interface HeaderProps extends WithStyles<typeof styles> {
  className?: string;
  title: ReactNode;
  subtitle?: ReactNode;
  actor: IdentityRefFragment;
  timestamp: string;
}

const Header = ({
  title,
  subtitle,
  actor,
  timestamp,
  classes,
  className,
}: HeaderProps) => {
  const { open, setOpen } = useContext(ActivityItemContext);
  const analytics = useAnalytics();

  const handleClick = () => {
    if (!open) {
      analytics.captureEvent(
        'flag-updates-card-expanded',
        typeof title === 'string' ? title : 'unknown',
      );
    }
    setOpen(!open);
  };

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
    <div className={classNames(classes.root, className)} onClick={handleClick}>
      <IdentityAvatar className={classes.avatar} identityRef={actor} />
      <div className={classes.content}>
        <Text as="div" variant="body-medium" color="secondary" truncate>
          <Text as="span" variant="body-medium" color="primary">
            {title}
          </Text>{' '}
          {subtitle && <>&bull; {subtitle}</>}
        </Text>
        <Text as="div" variant="body-small" color="secondary" truncate>
          {DateUtils.xAgo(new Date(timestamp))} &bull; {actor.displayName}
        </Text>
      </div>
      <RiArrowDownSLine
        className={classes.icon}
        aria-expanded={open}
        size="16"
      />
    </div>
  );
};

export default withStyles(styles)(Header);
